import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Users, 
  Plane, 
  Download, 
  BarChart3,
  ArrowLeft,
  Building
} from 'lucide-react';
import UserManagement from './UserManagement';
import ContractorManagement from './ContractorManagement';
import BaseManagement from './BaseManagement';
import SystemStats from './SystemStats';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

type TabType = 'stats' | 'users' | 'contractors' | 'bases' | 'backup';

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState<TabType>('stats');
  const [isDownloading, setIsDownloading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.role === 'ADMIN';
  const isSupervisor = user?.role === 'SUPERVISOR';

  // Only admins and supervisors can access
  if (!isAdmin && !isSupervisor) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Access Denied</h3>
          <p className="text-red-600 text-sm mt-1">Administrator or Supervisor access required.</p>
        </div>
      </div>
    );
  }

  const handleDownloadBackup = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get('/admin/backup/download', {
        responseType: 'blob',
        headers: {
          'Accept': 'application/sql'
        }
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `teaam-ems-backup-${new Date().toISOString().split('T')[0]}.sql`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Database backup downloaded successfully');
    } catch (error) {
      console.error('Backup download error:', error);
      toast.error('Failed to download backup');
    } finally {
      setIsDownloading(false);
    }
  };

  const allTabs = [
    { id: 'stats' as TabType, label: 'System Stats', icon: BarChart3 },
    { id: 'users' as TabType, label: 'Users', icon: Users, adminOnly: true },
    { id: 'contractors' as TabType, label: 'Air Carriers', icon: Plane },
    { id: 'bases' as TabType, label: 'Bases', icon: Building, adminOnly: true },
    { id: 'backup' as TabType, label: 'Backup', icon: Download, adminOnly: true }
  ];

  const tabs = allTabs.filter(tab => isAdmin || !tab.adminOnly);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Back to Dashboard"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <Shield className="h-8 w-8 text-indigo-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">Admin Panel</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    py-2 px-1 border-b-2 font-medium text-sm flex items-center
                    ${activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow">
          {activeTab === 'stats' && <SystemStats />}
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'contractors' && <ContractorManagement />}
          {activeTab === 'bases' && <BaseManagement />}
          {activeTab === 'backup' && (
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Database Backup</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-600 mb-4">
                  Download a complete backup of the database. This includes all missions, 
                  users, aircraft, and audit logs.
                </p>
                <button
                  onClick={handleDownloadBackup}
                  disabled={isDownloading}
                  className={`
                    flex items-center px-4 py-2 rounded-md text-white
                    ${isDownloading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                    }
                  `}
                >
                  <Download className="h-5 w-5 mr-2" />
                  {isDownloading ? 'Downloading...' : 'Download Backup'}
                </button>
                <div className="mt-6 text-sm text-gray-500">
                  <p className="font-medium mb-2">Note:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Backups are in PostgreSQL SQL format</li>
                    <li>File size depends on the amount of data</li>
                    <li>Regular backups are recommended</li>
                    <li>Store backups in a secure location</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}